import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

// Multibrand-UI Components
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import StyledContainer from '@zg-rentals/multibrand-ui/lib/StyledContainer';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Button from '@zg-rentals/multibrand-ui/lib/Button';

import { UserSelectors } from 'app/shared/selectors';

// TODO: update mui Row, Col to use zrs-comet
import { Container } from '@zillow/zrs-comet';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';

// Actions
import UserManagementActions from 'app/shared/actions/UserManagementActions';

// Components
import HeadWrapper from 'app/shared/components/HeadWrapper';

import LoadingConstants from 'app/shared/constants/LoadingConstants';
import UserPermissionsConstants from 'app/shared/constants/UserPermissionsConstants';

import pathsManifest from 'app/shared/pathsManifest';

import api from 'app/shared/utils/api';

import './style.scss';

const paths = pathsManifest.getAllPaths();

const DEFAULT_PAGE_TITLE = 'Welcome to Rental Manager';

const UserManagementInvitationPage = ({ dispatch, user }) => {
    const [errorMessage, setErrorMessage] = React.useState(null);
    const [loadingState, setLoadingState] = React.useState(LoadingConstants.INITIAL);
    const [companyInvite, setCompanyInvite] = React.useState(null);
    const [redirectTarget, setRedirectTarget] = React.useState(null);

    React.useEffect(() => {
        if (user.roles.includes(UserPermissionsConstants.PERMISSION_ROLES.COMPANY_ADMIN)) {
            setRedirectTarget(`${paths.userManagementPage}/${paths.userManagementUsers}`);
        } else if (user.roles.includes(UserPermissionsConstants.PERMISSION_ROLES.COMPANY_USER)) {
            setRedirectTarget(paths.propertiesPage);
        } else {
            setLoadingState(LoadingConstants.LOADING);
            dispatch(api.users.company.getUserManagementInvitation())
                .then((data) => {
                    setCompanyInvite(data.response);
                    setLoadingState(LoadingConstants.SUCCESS);
                })
                .catch(() => {
                    setErrorMessage('Failed to get invitation');
                    setLoadingState(LoadingConstants.ERROR);
                });
        }
    }, []);

    if (redirectTarget) return <Redirect to={redirectTarget} />;

    const acceptInvitation = () => {
        setLoadingState(LoadingConstants.LOADING);
        dispatch(UserManagementActions.onboardPendingUser())
            .then(() => {
                window.router.transitionTo(paths.propertiesPage);
            })
            .catch(() => {
                setLoadingState(LoadingConstants.ERROR);
            });
    };

    const declineInvitation = () => {
        setLoadingState(LoadingConstants.LOADING);
        dispatch(api.users.company.declineUserManagementInvitation())
            .then(() => {
                window.router.transitionTo(paths.propertiesPage);
            })
            .catch(() => {
                setErrorMessage('Failed to decline invitation');
                setErrorMessage(LoadingConstants.ERROR);
            });
    };

    const renderInvitation = () => (
        <>
            <Row>
                <Col>
                    <Text>
                        You&apos;re invited to join&nbsp;
                        <strong>{companyInvite.companyName}</strong>
                        . By accepting, all the properties
                        in your account will be added to the company and you will be
                        granted access to company properties.
                    </Text>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Text>
                        If you don&apos;t want your properties to be added to the company,
                        please decline the invite, contact support at
                        rentalshelp@zillowgroup.com to remove unwanted properties from your
                        account and then request your company admin to invite you again later.
                    </Text>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Button
                        type="primary"
                        onClick={acceptInvitation}
                    >
                        Accept Invitation
                    </Button>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Button
                        type="danger"
                        onClick={declineInvitation}
                    >
                        Decline Invitation
                    </Button>
                </Col>
            </Row>
        </>
    );

    const renderInfo = () => {
        if (companyInvite.isInvitationFound) return renderInvitation();
        return (
            <Row>
                <Col>
                    <Text>
                        No invitation found at this time.
                    </Text>
                </Col>
            </Row>
        );
    };

    return (
        <AuthCheckWrapper requireStrongAuthentication={false}>
            <div className="UserManagementInvitationPage">
                <Container className="UserManagementInvitationPage-container">
                    <StyledContainer
                        className="UserManagementInvitationPage-card"
                        borderFull={true}
                        borderRounded={true}
                    >
                        <HeadWrapper title={DEFAULT_PAGE_TITLE} />
                        <Row noPadding={true}>
                            <Col xs={12}>
                                <Text className="UserManagementInvitationPage-heading" type="h1">
                                    {DEFAULT_PAGE_TITLE}
                                </Text>
                            </Col>
                        </Row>
                        {loadingState === LoadingConstants.SUCCESS && renderInfo()}
                        {(loadingState === LoadingConstants.LOADING || loadingState === LoadingConstants.INITIAL) && (
                            <Row>
                                <Col>
                                    <SpinnerContainer />
                                </Col>
                            </Row>
                        )}
                        {loadingState === LoadingConstants.ERROR && (
                            <Row>
                                <Col>
                                    {errorMessage
                                        ? (
                                            <Text>
                                                {errorMessage}
                                            </Text>
                                        )
                                        : (
                                            <>
                                                <Text>
                                                    Failed to load your settings.
                                                </Text>
                                                <Text>
                                                    Please refresh, or check with your
                                                    company if you have an invitation.
                                                </Text>
                                            </>
                                        )
                                    }
                                </Col>
                            </Row>
                        )}
                    </StyledContainer>
                </Container>
            </div>
        </AuthCheckWrapper>
    );
};

const mapStateToProps = state => ({
    user: UserSelectors.makeUser(state),
});

export default connect(mapStateToProps)(UserManagementInvitationPage);
